import React, { useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listAuditTrail } from "../services/auditing";
import {
  AUDIT_ENTITIES,
  OPERATION_BADGE,
  OPERATION_LABEL_KEY,
  translateFieldLabel,
  formatFieldValue,
  isBasicField,
  changedFields,
  hasChanges,
} from "../utils/auditFormatting";

// Ported from sge_infantil_web/src/pages/auditing/index.js (antd Table +
// PageHeaderWrapper) into this project's plain Bootstrap conventions — same
// filters (period/operation/entidade/utilizador), same expand-to-diff row
// behaviour as ListaNotasLayer, same insert/update/delete detail views.
// Only Super Admin (005), Admin (002) and Director(a) (004) have the
// `configuracoes.auditoria` permission this screen is gated behind — see
// menuConfig.js and sge_infantil_core's Perfis e Permissões / seed scripts.

const toISODate = (date) => date.toISOString().slice(0, 10);
const todayISO = () => toISODate(new Date());
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toISODate(d);
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-PT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const AuditoriaLayer = () => {
  const { t } = useTranslation();

  const [dateFrom, setDateFrom] = useState(daysAgoISO(7));
  const [dateTo, setDateTo] = useState(todayISO());
  const [operation, setOperation] = useState("");
  const [entity, setEntity] = useState("");
  const [userName, setUserName] = useState("");

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [searched, setSearched] = useState(false);

  const fetchTrail = () => {
    if (!dateFrom || !dateTo) {
      toast.warning(t("auditoria.warning.dateRange"));
      return;
    }
    setLoading(true);
    setExpandedIdx(null);
    listAuditTrail({ dateFrom, dateTo, operation: operation || undefined, userName: userName || undefined })
      .then((resp) => {
        // Exclude update records with no actual (non-system) field changes —
        // same rule the old auditing page applied client-side.
        const data = (resp.data || []).filter(
          (r) => r.operation !== "update" || hasChanges(r.oldImage, r.newImage, r.entity),
        );
        setRecords(data);
        setSearched(true);
      })
      .catch(() => toast.error(t("auditoria.error.load")))
      .finally(() => setLoading(false));
  };

  const filteredRecords = useMemo(
    () => (entity ? records.filter((r) => r.entity === entity) : records),
    [records, entity],
  );

  const clearFilters = () => {
    setDateFrom(daysAgoISO(7));
    setDateTo(todayISO());
    setOperation("");
    setEntity("");
    setUserName("");
    setRecords([]);
    setSearched(false);
  };

  const toggleExpand = (idx) => setExpandedIdx((cur) => (cur === idx ? null : idx));

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex align-items-center gap-2 mb-16'>
              <span className='w-32-px h-32-px bg-primary-50 text-primary-600 rounded-circle d-flex justify-content-center align-items-center text-lg'>
                <Icon icon='mdi:filter-variant' />
              </span>
              <h6 className='text-md mb-0'>{t("global.filters")}</h6>
            </div>
            <div className='p-16 radius-8 bg-neutral-50'>
              <div className='row gy-3 align-items-end'>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("auditoria.field.dateFrom")}</label>
                  <input type='date' className='form-control' value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("auditoria.field.dateTo")}</label>
                  <input type='date' className='form-control' value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} />
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("auditoria.field.operation")}</label>
                  <select className='form-control form-select' value={operation} onChange={(e) => setOperation(e.target.value)}>
                    <option value=''>{t("auditoria.filter.allOperations")}</option>
                    <option value='insert'>{t("auditoria.operation.insert")}</option>
                    <option value='update'>{t("auditoria.operation.update")}</option>
                    <option value='delete'>{t("auditoria.operation.delete")}</option>
                  </select>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t("auditoria.field.entity")}</label>
                  <select className='form-control form-select' value={entity} onChange={(e) => setEntity(e.target.value)}>
                    <option value=''>{t("auditoria.filter.allEntities")}</option>
                    {AUDIT_ENTITIES.map((e) => (
                      <option key={e} value={e}>
                        {e}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("auditoria.field.userName")}</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder={t("auditoria.field.userName.placeholder")}
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                  />
                </div>
                <div className='col-sm-6 col-lg-1 d-flex gap-2'>
                  <button type='button' className='btn btn-primary-600 flex-grow-1' onClick={fetchTrail} disabled={loading} title={t("auditoria.action.search")}>
                    <Icon icon='ic:round-search' />
                  </button>
                  <button type='button' className='btn btn-outline-secondary' title={t("global.clean.fields")} onClick={clearFilters}>
                    <Icon icon='ic:round-refresh' />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex align-items-center gap-2 mb-16'>
              <Icon icon='mdi:clipboard-text-search-outline' className='text-primary-600 text-xl' />
              <h6 className='text-md mb-0'>{t("auditoria.trail.title")}</h6>
              <span className='badge bg-primary-600 text-white rounded-pill'>{filteredRecords.length}</span>
            </div>

            {loading ? (
              <div className='text-center py-40'>{t("global.loading")}</div>
            ) : !searched ? (
              <div className='alert alert-info mb-0'>{t("auditoria.hint.selectRange")}</div>
            ) : filteredRecords.length === 0 ? (
              <div className='alert alert-info mb-0'>{t("auditoria.hint.empty")}</div>
            ) : (
              <div className='table-responsive'>
                <table className='table bordered-table mb-0'>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }} />
                      <th>{t("auditoria.col.dateTime")}</th>
                      <th>{t("auditoria.col.userName")}</th>
                      <th>{t("auditoria.col.entity")}</th>
                      <th>{t("auditoria.col.operation")}</th>
                      <th>{t("auditoria.col.action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.map((record, idx) => {
                      const isExpanded = expandedIdx === idx;
                      return (
                        <React.Fragment key={idx}>
                          <tr role='button' onClick={() => toggleExpand(idx)} className={isExpanded ? "bg-neutral-50" : ""}>
                            <td>
                              <Icon icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"} className='text-secondary-light text-xl' />
                            </td>
                            <td>{formatDateTime(record.createdAt)}</td>
                            <td>
                              <Icon icon='mdi:account-outline' className='text-secondary-light me-4' />
                              {record.userName || "—"}
                            </td>
                            <td className='fw-semibold'>{record.entity || "—"}</td>
                            <td>
                              <span className={`badge ${OPERATION_BADGE[record.operation] || "bg-neutral-200 text-neutral-600"}`}>
                                {t(OPERATION_LABEL_KEY[record.operation]) || record.operation}
                              </span>
                            </td>
                            <td className='text-secondary-light'>
                              {record.action} <span className='fw-semibold text-neutral-900'>{record.entity}</span>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={6} className='bg-neutral-50 p-0'>
                                <div className='p-16'>
                                  <AuditDetail record={record} t={t} />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Shared field row ──────────────────────────────────────────────────────────
const FieldRow = ({ fieldKey, value, colorClass, entity }) => (
  <div className='d-flex align-items-start gap-3 py-8 border-bottom'>
    <span className='text-xs fw-semibold text-uppercase text-neutral-500' style={{ flex: "0 0 160px" }}>
      {translateFieldLabel(fieldKey)}
    </span>
    <span className={`text-sm ${colorClass}`} style={{ whiteSpace: "pre-line" }}>
      {formatFieldValue(fieldKey, value, entity)}
    </span>
  </div>
);

// ── UPDATE: side-by-side before/after ─────────────────────────────────────────
const UpdateDiff = ({ record, t }) => {
  const keys = changedFields(record.oldImage, record.newImage, record.entity);
  if (keys.length === 0) return null;

  return (
    <div>
      <div className='d-inline-flex align-items-center gap-2 mb-12 px-12 py-6 radius-8 bg-info-focus'>
        <Icon icon='mdi:swap-horizontal' className='text-info-main' />
        <span className='fw-semibold text-info-main'>{keys.length}</span>
        <span className='text-secondary-light'>
          {t(keys.length === 1 ? "auditoria.diff.fieldChanged" : "auditoria.diff.fieldsChanged")}
        </span>
      </div>
      <div className='row g-0 border radius-8 overflow-hidden'>
        <div className='col-6 border-end'>
          <div className='d-flex align-items-center gap-2 px-16 py-8 bg-danger-focus border-bottom'>
            <Icon icon='mdi:close-circle-outline' className='text-danger-main' />
            <span className='fw-bold text-xs text-uppercase'>{t("auditoria.diff.before")}</span>
          </div>
          <div className='px-16 bg-white'>
            {keys.map((k) => (
              <FieldRow key={k} fieldKey={k} value={(record.oldImage || {})[k]} colorClass='text-danger-main' entity={record.entity} />
            ))}
          </div>
        </div>
        <div className='col-6'>
          <div className='d-flex align-items-center gap-2 px-16 py-8 bg-success-focus border-bottom'>
            <Icon icon='mdi:check-circle-outline' className='text-success-main' />
            <span className='fw-bold text-xs text-uppercase'>{t("auditoria.diff.after")}</span>
          </div>
          <div className='px-16 bg-white'>
            {keys.map((k) => (
              <FieldRow key={k} fieldKey={k} value={(record.newImage || {})[k]} colorClass='text-success-main' entity={record.entity} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── INSERT: basic fields only ─────────────────────────────────────────────────
const InsertDetail = ({ record, t }) => {
  if (!record.newImage) return <span className='text-secondary-light'>{t("auditoria.detail.noInsertData")}</span>;
  const entries = Object.entries(record.newImage).filter(([k, v]) => isBasicField(k, v));
  if (entries.length === 0) return <span className='text-secondary-light'>{t("auditoria.detail.noBasicFields")}</span>;

  return (
    <div>
      <div className='d-flex align-items-center gap-2 mb-12'>
        <Icon icon='mdi:plus-circle-outline' className='text-success-main' />
        <span className='fw-semibold text-success-main'>{t("auditoria.detail.insertedData")}</span>
      </div>
      <div className='row'>
        <div className='col-md-6 border radius-8 bg-white'>
          {entries.map(([k, v]) => (
            <FieldRow key={k} fieldKey={k} value={v} colorClass='text-success-main' entity={record.entity} />
          ))}
        </div>
      </div>
    </div>
  );
};

// ── DELETE: all fields ────────────────────────────────────────────────────────
const DeleteDetail = ({ record, t }) => {
  if (!record.oldImage) return <span className='text-secondary-light'>{t("auditoria.detail.noDeleteData")}</span>;
  const entries = Object.entries(record.oldImage).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (entries.length === 0) return <span className='text-secondary-light'>{t("auditoria.detail.noFields")}</span>;

  return (
    <div>
      <div className='d-flex align-items-center gap-2 mb-12'>
        <Icon icon='mdi:delete-outline' className='text-danger-main' />
        <span className='fw-semibold text-danger-main'>{t("auditoria.detail.deletedData")}</span>
      </div>
      <div className='row'>
        <div className='col-md-6 border radius-8 bg-white'>
          {entries.map(([k, v]) => (
            <FieldRow key={k} fieldKey={k} value={v} colorClass='text-danger-main' entity={record.entity} />
          ))}
        </div>
      </div>
    </div>
  );
};

const AuditDetail = ({ record, t }) => {
  if (record.operation === "update") return <UpdateDiff record={record} t={t} />;
  if (record.operation === "insert") return <InsertDetail record={record} t={t} />;
  if (record.operation === "delete") return <DeleteDetail record={record} t={t} />;
  return <span className='text-secondary-light'>{t("auditoria.detail.unknownOperation")}</span>;
};

export default AuditoriaLayer;
